import { GoogleOAuthProvider } from "@react-oauth/google";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "./components/ui/sonner";
import { AuthProvider, useAuth } from "./context/AuthContext";
import LoginPage from "./pages/LoginPage";
import RegisterPage from "./pages/RegisterPage";
import ForgotPasswordPage from "./pages/ForgotPasswordPage";
import ResetPasswordPage from "./pages/ResetPasswordPage";
import DashboardPage from "./pages/DashboardPage";
import CosplaysPage from "./pages/CosplaysPage";
import CosplayDetailPage from "./pages/CosplayDetailPage";
import HistoryPage from "./pages/HistoryPage";
import SocialPage from "./pages/SocialPage";
import CommunityCosplayPage from "./pages/CommunityCosplayPage";
import SettingsPage from "./pages/SettingsPage";
import SharedCosplayPage from "./pages/SharedCosplayPage";
import AdminPage from "./pages/AdminPage";
import CreateDashboardPage from "./pages/CreateDashboardPage";
import PublicCreatorPage from "./pages/PublicCreatorPage";
import ImportMandomaster from "./pages/ImportMandomaster";
import ProfilePage from "./pages/ProfilePage";
import ExplorePage from "./pages/ExplorePage";
import LandingPage from "./pages/LandingPage";
import CommissionPage from "./pages/CommissionPage";
import GroupsPage from "./pages/GroupsPage";
import GroupDetailPage from "./pages/GroupDetailPage";
import ConsPage from "./pages/ConsPage";
import ConDetailPage from "./pages/ConDetailPage";
import Layout from "./components/Layout";

// Catch-all component to handle @username URLs
const CatchAllRoute = () => {
  const location = useLocation();
  const path = location.pathname;
  
  // Check if path starts with /@
  if (path.startsWith('/@')) {
    const username = path.slice(2); // Remove /@ prefix
    return <Navigate to={`/c/${username}`} replace />;
  }
  
  // For any other unmatched route, redirect to login
  return <Navigate to="/login" replace />;
};

const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-cyan-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to="/login" replace />;
  }
  
  return children;
};

const PublicRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-cyan-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }
  
  if (user) {
    const redirect = localStorage.getItem('postLoginRedirect');
    if (redirect) {
      localStorage.removeItem('postLoginRedirect');
      return <Navigate to={redirect} replace />;
    }
    return <Navigate to="/dashboard" replace />;
  }
  
  return children;
};

// Protected route for creator-only pages
const CreatorRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-cyan-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to="/login" replace />;
  }
  
  if (!user.creator_role) {
    return <Navigate to="/dashboard" replace />;
  }
  
  return children;
};

function App() {
  return (
    <GoogleOAuthProvider clientId={process.env.REACT_APP_GOOGLE_CLIENT_ID || ""}>
    <AuthProvider>
      <div className="noise-bg">
        <BrowserRouter basename={process.env.PUBLIC_URL || undefined}>
          <Routes>
            <Route path="/login" element={
              <PublicRoute><LoginPage /></PublicRoute>
            } />
            <Route path="/register" element={
              <PublicRoute><RegisterPage /></PublicRoute>
            } />
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
            <Route path="/reset-password" element={<ResetPasswordPage />} />
            <Route path="/share/:shareToken" element={<SharedCosplayPage />} />
            {/* Public, no-login MandoMaster build gallery (funnel landing) */}
            <Route path="/explore" element={<ExplorePage />} />
            {/* STAGING preview of the marketing homepage (root "/" unchanged until approved) */}
            <Route path="/home-preview" element={<LandingPage />} />
            <Route path="/admin" element={<AdminPage />} />
            {/* Public creator profile - /c/username format */}
            <Route path="/c/:username" element={<PublicCreatorPage />} />
            {/* Public commission request form */}
            <Route path="/commission" element={<CommissionPage />} />
            {/* Public marketing homepage at the root */}
            <Route path="/" element={<LandingPage />} />
            {/* App entry alias: signed in -> dashboard, signed out -> login */}
            <Route path="/app" element={
              <ProtectedRoute>
                <Navigate to="/dashboard" replace />
              </ProtectedRoute>
            } />
            <Route path="/dashboard" element={
              <ProtectedRoute>
                <Layout>
                  <DashboardPage />
                </Layout>
              </ProtectedRoute>
            } />
            <Route path="/cosplays" element={
              <ProtectedRoute>
                <Layout>
                  <CosplaysPage />
                </Layout>
              </ProtectedRoute>
            } />
            <Route path="/cosplays/:id" element={
              <ProtectedRoute>
                <Layout>
                  <CosplayDetailPage />
                </Layout>
              </ProtectedRoute>
            } />
            <Route path="/history" element={
              <ProtectedRoute>
                <Layout>
                  <HistoryPage />
                </Layout>
              </ProtectedRoute>
            } />
            <Route path="/social" element={
              <ProtectedRoute>
                <Layout>
                  <SocialPage />
                </Layout>
              </ProtectedRoute>
            } />
            <Route path="/groups" element={
              <ProtectedRoute>
                <Layout>
                  <GroupsPage />
                </Layout>
              </ProtectedRoute>
            } />
            <Route path="/groups/:id" element={
              <ProtectedRoute>
                <Layout>
                  <GroupDetailPage />
                </Layout>
              </ProtectedRoute>
            } />
            <Route path="/cons" element={
              <ProtectedRoute>
                <Layout>
                  <ConsPage />
                </Layout>
              </ProtectedRoute>
            } />
            <Route path="/cons/:id" element={
              <ProtectedRoute>
                <Layout>
                  <ConDetailPage />
                </Layout>
              </ProtectedRoute>
            } />
            <Route path="/social/:id" element={
              <ProtectedRoute>
                <Layout>
                  <CommunityCosplayPage />
                </Layout>
              </ProtectedRoute>
            } />
            <Route path="/settings" element={
              <ProtectedRoute>
                <Layout>
                  <SettingsPage />
                </Layout>
              </ProtectedRoute>
            } />
            {/* Creator Dashboard - only for users with creator_role */}
            <Route path="/create" element={
              <CreatorRoute>
                <Layout>
                  <CreateDashboardPage />
                </Layout>
              </CreatorRoute>
            } />
            {/* Catch-all route for @username URLs and unmatched paths */}
            <Route path="/profile" element={
              <ProtectedRoute>
                <Layout>
                  <ProfilePage />
                </Layout>
              </ProtectedRoute>
            } />
            <Route path="/import/mandomaster" element={<ImportMandomaster />} />
            <Route path="*" element={<CatchAllRoute />} />
          </Routes>
        </BrowserRouter>
        <Toaster 
          position="top-right" 
          toastOptions={{
            style: {
              background: 'rgba(15, 23, 42, 0.9)',
              border: '1px solid rgba(255, 255, 255, 0.1)',
              color: '#e2e8f0',
              backdropFilter: 'blur(24px)',
            },
          }}
        />
      </div>
    </AuthProvider>
    </GoogleOAuthProvider>
  );
}

export default App;
